import { useNavigate } from 'react-router-dom';
import { useWeekEvents } from '../hooks/useEventData';
import { useWeekAvailability, useWeeklyPlan, usePlanFeedback } from '../hooks/useAvailability';
import { useAuth } from '../context/AuthContext';
import { formatTime, getWeekDates, isToday } from '../utils/helpers';
import EventBadge from '../components/EventBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import AvailabilityToggle from '../components/AvailabilityToggle';
import AvailabilityBanner from '../components/AvailabilityBanner';
import AdminAvailabilitySummary from '../components/AdminAvailabilitySummary';
import WeeklyPlanPanel from '../components/WeeklyPlanPanel';
import PlanFeedbackPanel from '../components/PlanFeedbackPanel';
import { useGroupData } from '../hooks/useGroupData';
import type { CalendarEvent, EventType, AvailabilityStatus } from '../types';

const borderColors: Record<EventType, string> = {
  practice: 'border-l-practice',
  regatta: 'border-l-regatta',
  competition: 'border-l-competition',
};

export default function WeeklyViewB() {
  const { events, loading, error } = useWeekEvents();
  const { user } = useAuth();
  const navigate = useNavigate();
  const weekDates = getWeekDates();

  const activeEvents = events.filter(e => e.status === 'on');
  const { availability, setMyAvailability } = useWeekAvailability(activeEvents);
  const { plan, generating, generate, share, publish, editAssignment } = useWeeklyPlan();
  const { feedback, summary: feedbackSummary, submitting: feedbackSubmitting, submit: submitFeedback } = usePlanFeedback(plan?.id || null);
  const myFeedback = feedback.find(f => f.userId === user?.id) || null;

  const eventsByDate = new Map<string, CalendarEvent>();
  events.forEach(e => eventsByDate.set(e.date, e));

  const isAdmin = user?.role === 'admin';
  const { members } = useGroupData();

  const missingCount = activeEvents.filter(e => {
    const records = availability[e.id] || [];
    return !records.some(r => r.userId === user?.id);
  }).length;

  const handleAvailability = async (eventId: string, status: AvailabilityStatus) => {
    await setMyAvailability(eventId, status);
  };

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  const monthYear = new Date(weekDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  return (
    <div className="px-4 pt-5 pb-6 max-w-lg mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900 dark:text-white font-mono tracking-tight">Week View</h1>
        <span className="text-xs font-mono text-gray-400">{monthYear}</span>
      </div>

      <AvailabilityBanner missingCount={missingCount} />
      {isAdmin && <WeeklyPlanPanel plan={plan} generating={generating} onGenerate={generate} onShare={share} onPublish={publish} feedback={feedback} feedbackSummary={feedbackSummary} members={members} onEditAssignment={editAssignment} />}
      {!isAdmin && plan && plan.status === 'pending_review' && (
        <PlanFeedbackPanel plan={plan} myFeedback={myFeedback} submitting={feedbackSubmitting} onSubmit={submitFeedback} />
      )}

      {/* Day header strip */}
      <div className="grid grid-cols-7 gap-1 mb-3">
        {weekDates.map(date => {
          const d = new Date(date + 'T00:00:00');
          const dayName = d.toLocaleDateString('en-US', { weekday: 'narrow' });
          const dayNum = d.getDate();
          const today = isToday(date);
          const hasEvent = eventsByDate.has(date);

          return (
            <div key={date} className={`text-center py-1.5 rounded ${
              today ? 'bg-practice text-white' : ''
            }`}>
              <p className={`text-[10px] font-bold uppercase ${
                today ? 'text-white/80' : 'text-gray-400'
              }`}>{dayName}</p>
              <p className={`text-sm font-bold ${
                today ? 'text-white' : hasEvent ? 'text-gray-900 dark:text-white' : 'text-gray-300 dark:text-gray-700'
              }`}>{dayNum}</p>
              {hasEvent && !today && (
                <div className={`w-1.5 h-1.5 rounded-full mx-auto mt-0.5 ${
                  eventsByDate.get(date)?.status === 'cancelled' ? 'bg-cancelled' :
                  eventsByDate.get(date)?.type === 'practice' ? 'bg-practice' :
                  eventsByDate.get(date)?.type === 'regatta' ? 'bg-regatta' : 'bg-competition'
                }`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Event list */}
      <div className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 divide-y divide-gray-50 dark:divide-gray-800/50">
        {weekDates.map(date => {
          const d = new Date(date + 'T00:00:00');
          const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
          const event = eventsByDate.get(date);
          const today = isToday(date);

          if (!event) {
            return (
              <div key={date} className="flex items-center px-4 py-2.5 min-h-[44px]">
                <span className={`w-20 text-xs font-mono flex-shrink-0 ${
                  today ? 'text-practice font-bold' : 'text-gray-300 dark:text-gray-600'
                }`}>{dayLabel}</span>
                <span className="text-xs text-gray-200 dark:text-gray-800">&mdash;</span>
              </div>
            );
          }

          const myAvail = event.status === 'on'
            ? (availability[event.id] || []).find(r => r.userId === user?.id)?.status || null
            : null;

          return (
            <div key={date} className={`${today ? 'bg-practice/5 dark:bg-practice/5' : ''}`}>
              <button
                onClick={() => navigate(`/event/${event.id}`)}
                className="w-full flex items-center gap-2 px-0 py-0 min-h-[52px] text-left hover:bg-gray-50 dark:hover:bg-gray-800/30 active:bg-gray-100 dark:active:bg-gray-800 transition-colors"
              >
                <div className={`w-1 self-stretch ${
                  event.status === 'cancelled' ? 'bg-cancelled' : borderColors[event.type].replace('border-l-', 'bg-')
                }`} />

                <span className={`w-[72px] text-xs font-mono flex-shrink-0 pl-2 ${
                  today ? 'text-practice font-bold' : 'text-gray-500 dark:text-gray-400'
                }`}>{dayLabel}</span>

                <div className="flex-1 min-w-0 py-2">
                  <div className="flex items-center gap-1.5">
                    <EventBadge type={event.type} status={event.status} size="sm" />
                    {event.title && (
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{event.title}</span>
                    )}
                  </div>
                  {event.status !== 'cancelled' && (
                    <p className="text-xs text-gray-400 font-mono mt-0.5">
                      {formatTime(event.startTime)}-{formatTime(event.endTime)} &bull; {event.locationName}
                    </p>
                  )}
                </div>

                <svg className="w-4 h-4 text-gray-300 dark:text-gray-700 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              {/* Availability row */}
              {event.status === 'on' && (
                <div className="px-4 py-2 border-t border-gray-50 dark:border-gray-800/50">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-mono text-gray-400 dark:text-gray-500 uppercase">Avail</span>
                    <AvailabilityToggle status={myAvail} onChange={(s) => handleAvailability(event.id, s)} compact />
                  </div>
                  {isAdmin && (
                    <AdminAvailabilitySummary members={members} records={availability[event.id] || []} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
